import { Logger } from 'oidc-client-ts'
import type { INavigator, IWindow } from 'oidc-client-ts'
import type { UserManagerSettingsStore } from 'oidc-client-ts'
import { Browser } from '@capacitor/browser'
import { Capacitor } from '@capacitor/core'

/**
 * Capacitor-aware RedirectNavigator.
 * Falls back to default behavior if not running inside Capacitor.
 */
export class Navigator implements INavigator {
  private readonly _logger = new Logger('Navigator')

  constructor(private _settings: UserManagerSettingsStore) {}

  public async prepare({
    redirectMethod = this._settings.redirectMethod,
    redirectTarget = this._settings.redirectTarget,
  }: {
    redirectMethod?: 'replace' | 'assign'
    redirectTarget?: 'top' | 'self'
  } = {}): Promise<IWindow> {
    this._logger.create('prepare')

    let targetWindow = window.self as Window
    if (redirectTarget === 'top') {
      targetWindow = window.top ?? window.self
    }

    const redirect = targetWindow.location[redirectMethod].bind(
      targetWindow.location,
    ) as (url: string) => never
    let abort: (reason: Error) => void

    return {
      navigate: async (params): Promise<never> => {
        this._logger.create('navigate')

        const promise = new Promise((_, reject) => {
          abort = reject
        })

        if (Capacitor.isNativePlatform()) {
          // Native app — open URL in Capacitor Browser
          await Browser.open({ url: params.url })
          // We cannot block here the same way (because it's async),
          // so just return a pending promise forever
          return await (promise as Promise<never>)
        } else {
          // Web — perform normal redirect
          redirect(params.url)
          return await (promise as Promise<never>)
        }
      },
      close: () => {
        this._logger.create('close')
        abort?.(new Error('Redirect aborted'))
        targetWindow.stop()
      },
    }
  }

  public async callback(): Promise<void> {
    return
  }
}
